import React, { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Badge } from '../../../../components/ui/badge'
import ProfilePanel from './ProfilePanel'
import DocumentFieldsTable from './DocumentFieldsTable'
import { LIST_PREVIEW_LIMIT } from './profileDetailSections'

import { formatSydneyDate } from '../../../../utils/sydneyDateTime'
function statusBadgeClass(status) {
  const s = String(status || '').toUpperCase()
  if (s === 'VERIFIED' || s === 'READY') return 'bg-green-100 text-green-800 border-green-200'
  if (s === 'FAILED' || s === 'REJECTED') return 'bg-red-100 text-red-800 border-red-200'
  if (s === 'PENDING') return 'bg-yellow-100 text-yellow-800 border-yellow-200'
  return 'bg-slate-100 text-slate-700 border-slate-200'
}

function formatCaptured(at) {
  if (!at) return null
  try {
    return formatSydneyDate(new Date(at))
  } catch {
    return String(at)
  }
}

function DocCard({ doc, profileDocumentViewLink }) {
  const [open, setOpen] = useState(Boolean(doc.defaultOpen))

  return (
    <div className={`border border-slate-200 rounded-lg mb-2.5 overflow-hidden ${open ? 'open' : ''}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-3.5 py-3 flex items-start justify-between gap-3 bg-slate-50 hover:bg-slate-100 border-0 cursor-pointer"
      >
        <div className="min-w-0 flex-1">
          <div className="font-bold text-sm text-slate-900">{doc.title}</div>
          <div className="text-xs text-slate-500 mt-1">
            {[doc.subtitle, doc.meta, doc.capturedAt ? `captured ${formatCaptured(doc.capturedAt)}` : null]
              .filter(Boolean)
              .join(' · ')}
          </div>
          {doc.warning && (
            <div className="text-xs text-red-700 font-semibold mt-1.5">⚠ {doc.warning}</div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {doc.status && (
            <Badge className={`text-[11px] ${statusBadgeClass(doc.status)}`}>{doc.status}</Badge>
          )}
          {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
        </div>
      </button>
      {open && (
        <div className="px-3.5 pb-3.5 border-t border-slate-200 bg-white">
          <p className="text-xs text-slate-500 mt-3 mb-0">
            Extracted fields — images may be purged per retention policy
          </p>
          <DocumentFieldsTable rows={doc.fields} />
          {doc.document && profileDocumentViewLink ? (
            <div className="mt-3">{profileDocumentViewLink(doc.document)}</div>
          ) : null}
        </div>
      )}
    </div>
  )
}

export default function ProfileIdentityDocuments({
  documents,
  profileDocumentViewLink,
  previewLimit = LIST_PREVIEW_LIMIT,
}) {
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? documents : documents.slice(0, previewLimit)
  const hiddenCount = Math.max(0, documents.length - previewLimit)

  return (
    <ProfilePanel
      title="Identity documents"
      hint={documents.length ? `${documents.length} on record` : null}
    >
      {documents.length === 0 ? (
        <p className="text-sm text-slate-500 m-0">No identity documents on record.</p>
      ) : (
        <>
          {visible.map((doc) => (
            <DocCard key={doc.id} doc={doc} profileDocumentViewLink={profileDocumentViewLink} />
          ))}
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-1 text-teal-700 text-sm font-semibold hover:underline bg-transparent border-0 cursor-pointer p-0"
            >
              {expanded ? 'Show less' : `Show ${hiddenCount} more`}
            </button>
          )}
        </>
      )}
    </ProfilePanel>
  )
}
